'use client';

import { Tab } from '@common/components';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { UnauthQueryOptions } from '@/api';
import { useScrollToSection } from '@/app/donation/_hooks/useScrollToSection';
import { DONATION_ROUTES } from '@/constants';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useMediaQuery } from '@/hooks/useMediaQuery';

import { TABS, TABS_LABELS } from '../constants';

interface DetailTabProps {
  className?: string;
  businessId: number;
}

export const DetailTabComponent = ({
  className,
  businessId,
}: DetailTabProps) => {
  const router = useCustomRouter();
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const { scrollToSectionWithTab } = useScrollToSection();
  const searchParams = useSearchParams();

  const [tab, setTab] = useState(TABS.BASIC);

  // 초기 마운트 시 searchParams의 tab이 있으면 해당 섹션으로 스크롤
  useEffect(() => {
    if (searchParams?.get('tab')) {
      scrollToSectionWithTab(searchParams.get('tab') as TABS, {
        behavior: 'instant',
      });
      setTab(searchParams.get('tab') as TABS);
    }
  }, [scrollToSectionWithTab, searchParams]);

  const { data } = useQuery({
    ...UnauthQueryOptions[
      'get:/wello/v2/allows/home/donation-business/{donationBusinessId}/detail-count'
    ]({
      path: {
        donationBusinessId: businessId,
      },
    }),
    select: (data) => data.context,
    placeholderData: keepPreviousData,
  });

  return (
    <Tab
      className={className}
      size={isDesktopViewport ? 'lg' : 'sm'}
      variant="full"
      value={tab ?? TABS.BASIC}
      onChange={(value) => {
        router.replace(
          DONATION_ROUTES.HOMETOWN_BUSINESS_DETAIL.withSearchParams({
            searchParams: {
              ...searchParams,
              tab: value,
            },
            dynamicPath: {
              id: businessId,
            },
          }),
        );
      }}
    >
      <Tab.Item value={TABS.BASIC}>{TABS_LABELS[TABS.BASIC]}</Tab.Item>
      <Tab.Item value={TABS.DONATIONS}>
        {TABS_LABELS[TABS.DONATIONS]} {data?.donationCount?.toLocaleString()}
      </Tab.Item>
      <Tab.Item value={TABS.COMMENTS}>
        {TABS_LABELS[TABS.COMMENTS]}{' '}
        {data?.donationBusinessCommentCount?.toLocaleString()}
      </Tab.Item>
    </Tab>
  );
};

export const DetailTab = (props: DetailTabProps) => {
  return (
    <ErrorBoundary fallback={null}>
      <Suspense>
        <DetailTabComponent {...props} />
      </Suspense>
    </ErrorBoundary>
  );
};
